import { ReactNode, useState } from 'react';

import { IMedication } from '@/shared/types/interfaces/medication';
import { Box, Button } from '@/shared/ui';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import SelectMedication from '../select-medication';
import RestrictedMedicationPopup from '../restricted-medication-popup';
import { useAppSelector } from '@/shared/hooks/store';
import { selectUserUserOnboarding } from '@/store/slices/onboarding/selectors';
import { isC2Medication, isGLPMedication } from '@/shared/helpers/medications';
import { CustomersStepTypeEnum } from '@/shared/endpoints/customers';
import FillMedboxPic from '@/assets/images/medbox/fill-medbox.svg';

export interface IAddMedicationProps {
    zipCode: string;
    onSelect(medication: IMedication): void;
    onBack(): void;
}

const AddMedication = ({ onSelect, onBack }: IAddMedicationProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
    const userOnboarding = useAppSelector(selectUserUserOnboarding);
    const [showOtcMedPrescriptionForm, setShowOtcMedPrescriptionForm] = useState(false);
    const [restrictedMedication, setRestrictedMedication] = useState<IMedication | null>(null);

    const handleSelectMedication = (medication: IMedication) => {
        if (
            (isC2Medication(medication) || isGLPMedication(medication)) &&
            userOnboarding?.details?.currentStep === CustomersStepTypeEnum.Review
        ) {
            setRestrictedMedication(medication);
            return;
        }

        if (medication.otc) {
            setShowOtcMedPrescriptionForm(false);
        }
        onSelect(medication);
    };

    const handleGoBack = () => {
        setShowOtcMedPrescriptionForm(false);

        if (!showOtcMedPrescriptionForm) {
            onBack();
        }
    };

    return (
        <>
            <MedicationFlowBox>
                <span className='block font-agrandir text-24 sm:text-28 leading-[133%] sm:leading-[128%] mb-[9px] sm:mb-[25px]'>Add medication</span>
                <SelectMedication onSelect={handleSelectMedication} />
                {restrictedMedication && (
                    <RestrictedMedicationPopup
                        onClose={() => setRestrictedMedication(null)}
                        medications={[restrictedMedication]} 
                        isFromAddMedication
                        />
                )}

                {!isMobile && (
                    <Button
                        fontClassName="font-dmSansMedium font-bold text-17 leading-[130%]"
                        testId="button-medications-select-back"
                        variant="text"
                        className="prescriptions__add-medication__back mt-[25px] w-fit mr-auto"
                        onClick={handleGoBack}
                    >
                        {`< Back`}
                    </Button>
                )}
            </MedicationFlowBox>
            {isMobile && (
                <Button
                    fontClassName="font-dmSansMedium font-bold text-17 leading-[130%]"
                    testId="button-medications-select-back"
                    variant="text"
                    className="prescriptions__add-medication__back mt-[20px] w-full"
                    onClick={handleGoBack}
                >
                    {`< Back`}
                </Button>
            )}

            <img src={FillMedboxPic} alt="Fill Medbox" className="w-full max-w-[200px] mt-[25px] mx-auto block xl:hidden" />
        </>
    );
};

export const MedicationFlowBox = ({ children, ...rest }: { children: ReactNode }) => {
    const theme = useTheme();

    return (
        <Box
            bgWhite
            shadow
            data-testid="add-medication"
            sx={{
                maxWidth: '545px',
                width: '100%',
                height: 'min-content',
                // '@media only screen and (min-width: 1441px)': {
                //     width: '625px'
                // },
                '&&': {
                    [theme.breakpoints.down('sm')]: {
                        padding: '17px'
                    },
                    [theme.breakpoints.up('sm')]: {
                        padding: '25px'
                    },
                }
            }
            }
            {...rest}
        >
            {children}
        </Box>
    );
};

export default AddMedication;
